import { Survey, SurveyQuestionType } from 'posthog-js';
import { USER_SURVEY_ANSWER_MIN_LENGTH } from 'src/components/UserSurvey/consts';

export function getHasSeenSurveyKey(surveyId: string) {
	return `hasSeenSurvey_${surveyId}`;
}

export function storeHasSeenSurvey(surveyId: string) {
	localStorage.setItem(getHasSeenSurveyKey(surveyId), 'true');
}

interface ParsedDescription {
	type: 'textarea' | 'input';
	placeholder: string;
}

/**
 * At the time of comment, PostHog doesn't allow specifying the field type
 * (e.g. textarea vs input) nor the placeholder; so, we're encoding these
 * values in the optional `description` field with the following pattern:
 * `type=textarea|placeholder=Some placeholder text`.
 *
 * This function parses `description` into a key-value pair object.
 */
export function parsePostHogSurveyDescription(
	description: string | null | undefined
) {
	const result: ParsedDescription = {
		type: 'textarea',
		placeholder: 'Your answer',
	};

	if (!description) {
		return result;
	}

	// Split the string into key-value pairs.
	const pairs = description.split('|');

	for (const pair of pairs) {
		const [key, value] = pair.split('=');

		if (key && value !== undefined) {
			const keyTrimmed = key.trim();
			const valueTrimmed = value.trim();

			if (
				keyTrimmed === 'type' &&
				(valueTrimmed === 'textarea' || valueTrimmed === 'input')
			) {
				result.type = valueTrimmed;
			} else if (keyTrimmed === 'placeholder') {
				result.placeholder = valueTrimmed;
			}
		}
	}

	return result;
}

interface ValidationRule {
	type: string;
	value?: number;
}

interface ValidateAnswerOptions {
	type: SurveyQuestionType;
	optional: boolean | undefined;
	validation?: ValidationRule[];
}

export function validatePostHogSurveyAnswer(
	answer: string,
	{ type, optional, validation }: ValidateAnswerOptions
) {
	if (optional) {
		return true;
	}

	if (type === 'single_choice' && answer) {
		return true;
	}

	// Check if there's validation configured via the PostHog dash.
	if (validation?.length) {
		return validation.every((rule) => {
			if (!rule.value) {
				return true;
			}

			switch (rule.type) {
				case 'min_length':
					return answer.length >= rule.value;
				case 'max_length':
					return answer.length <= rule.value;
				default:
					return true;
			}
		});
	}

	// Else, run default validation.
	return answer.length >= USER_SURVEY_ANSWER_MIN_LENGTH;
}

export function getPostHogSurveyPageTitle(survey: Survey, currentPage: number) {
	if (currentPage === 0) {
		// The survey name may include a "<>" for differentiating it in the PostHog dash
		// (e.g. "We want your feedback! <> <cohort>"). So we split the name just in case.
		const splitSurveyName = survey.name.split('<>');

		return splitSurveyName[0]?.trim() || 'Help us improve Drift!';
	}

	const isOnQuestionPage =
		currentPage > 0 && currentPage <= survey.questions.length;

	const currentQuestion = isOnQuestionPage
		? survey.questions[currentPage - 1]
		: undefined;

	if (currentQuestion) {
		return currentQuestion.question;
	}

	return (
		survey.appearance?.thankYouMessageHeader || 'Thank you for your feedback!'
	);
}
